import styled from 'styled-components';

import { Locale } from 'modules/domains/template';
import { FlagIcon } from 'ui/flags';
import { colors, spacing } from 'ui/theme';

export const getLocaleLabel = (locale: Locale) =>
  `${locale?.languageName ?? ''} ${locale?.tag ?? ''}`.trim();

export const findCountryCode = (tag = '') => {
  // Accounts for the following characters: ';', ':', '_' and will convert to a dash
  const rule = /[_;:]/g;
  const modified = tag?.replace(rule, '-');
  const arr = modified?.split('-');

  switch (arr?.length) {
    // A case where we have language and a region en-US
    case 2:
      return arr[1]?.toUpperCase();
    // Resolves if a user enters a locale in ICU format
    case 3:
      return arr[2]?.toUpperCase();
    default:
      return 'default';
  }
};

export const LOCALE_LABEL_TEST_IDS = {
  label: 'locale-label',
  labelIcon: 'locale-label-icon',
  labelText: 'locale-label-text',
};

const LabelContainer = styled.div`
  display: flex;
  align-items: center;

  &&[data-disabled="true"] {
    color: ${colors.textDisabled};
    img {
      opacity: 0.4;
      filter: grayscale(100%);
    }
  }

  &&[data-available="true"] {
    img {
      opacity: 0.4;
    }
  }
`;

const TextWrapper = styled.span`
  margin-left: ${spacing.half};
  color: inherit;
`;

type LocaleLabelProps = {
  locale: Locale;
  isDisabled?: boolean;
  isAvailableLocale?: boolean;
};

/**
 * A user must include a two letter alpha-2 country code to display a flag by that locale.
 * If no two letter alpha-2 country code, we'll display a generic locale icon.
 */
export const LocaleLabel = ({ locale, isDisabled, isAvailableLocale }: LocaleLabelProps) => {
  return (
    <LabelContainer
      data-test={LOCALE_LABEL_TEST_IDS.label}
      data-disabled={!!isDisabled}
      data-available={!!isAvailableLocale}
    >
      <FlagIcon
        code={findCountryCode(locale?.tag)}
        data-test={LOCALE_LABEL_TEST_IDS.labelIcon}
        size={16}
      />
      <TextWrapper data-test={LOCALE_LABEL_TEST_IDS.labelText}>
        {getLocaleLabel(locale)}
      </TextWrapper>
    </LabelContainer>
  );
};
